import {
  MIN_ARRAY_LENGTH_FOR_INNER_CALCULATION,
  MIN_WAYPOINTS_COUNT_FOR_REORDER,
  MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD,
} from "../../constants";
import {
  IRoutingWaypointStationRow,
  IStationRouteStationInfo,
  IStationRouteWaypointStationLike,
} from "../../models";
import {
  ensureRoutingWaypointsAreInterior,
  filterRealRouteStations,
  isMlPathRoutingWaypoint,
  isStationRouteWaypointStation,
} from "../routing-waypoint";

/** True when the route has at least two passenger stations — waypoints must stay interior. */
export const canInsertStationRouteWaypoint = (
  stations: IStationRouteWaypointStationLike[] | undefined,
): boolean =>
  filterRealRouteStations(stations).length >=
  MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD;

/** Waypoint drag only when it can move to another interior slot (extra real station or another waypoint). */
export const canReorderRoutingWaypoint = (
  stations: IStationRouteWaypointStationLike[] | undefined,
): boolean => {
  const list = stations ?? [];
  const realCount = filterRealRouteStations(list).length;
  const waypointCount = list.filter(isStationRouteWaypointStation).length;

  return (
    realCount >= MIN_ARRAY_LENGTH_FOR_INNER_CALCULATION ||
    waypointCount >= MIN_WAYPOINTS_COUNT_FOR_REORDER
  );
};

/** User-owned interior waypoint that can be reordered; ML path waypoints stay fixed. */
export const isRoutingWaypointDragOrderable = ({
  station,
  isLocked,
  isFirstStation,
  isLastStation,
  stations,
}: {
  station?: IRoutingWaypointStationRow;
  isLocked?: boolean;
  isFirstStation?: boolean;
  isLastStation?: boolean;
  stations?: IStationRouteWaypointStationLike[];
}): boolean => {
  if (isLocked || isFirstStation || isLastStation) {
    return false;
  }

  if (
    !isStationRouteWaypointStation(station) ||
    isMlPathRoutingWaypoint(station)
  ) {
    return false;
  }

  return canReorderRoutingWaypoint(stations);
};

export const insertStationRouteWaypoint = <
  T extends IStationRouteWaypointStationLike,
>({
  stations,
  waypoint,
  insertAfterIndex,
}: {
  stations: T[];
  waypoint: T;
  insertAfterIndex: number;
}): T[] => {
  if (!canInsertStationRouteWaypoint(stations)) {
    return [...stations];
  }

  const insertAt = Math.min(Math.max(insertAfterIndex + 1, 0), stations.length);
  const updated = [...stations];
  updated.splice(insertAt, 0, waypoint);
  return ensureRoutingWaypointsAreInterior(updated);
};

/**
 * Real station order / drag is locked only when the ML path is opened from a ride.
 * Standalone edit by id stays reorderable even if the path was originally saved from a ride.
 */
export const isStationRouteRouteStructureLocked = (
  rideOpenerId?: number,
): boolean => Boolean(rideOpenerId);

/**
 * Waypoint add/delete is only allowed on customized ML paths in standalone edit —
 * not when opened from a ride card, and not on system-cache (customized=false) routes.
 */
export const isStationRouteWaypointEditingAllowed = (
  rideOpenerId?: number,
  customized?: boolean,
): boolean => !rideOpenerId && Boolean(customized);

export const getStationRouteRealStations = (
  stations: IStationRouteStationInfo[] | undefined,
): IStationRouteStationInfo[] =>
  (stations || []).filter(station => !isStationRouteWaypointStation(station));

export const countStationRouteRealStations = (
  stations: IStationRouteStationInfo[] | undefined,
): number => getStationRouteRealStations(stations).length;
